import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["classes", "onExpand", "onClick", "layout", "children", "rowSelectionEnabled", "rowSelectionOptions", "selectedRowIds", "onRowSelectionChange"];
var _expansionPanelStyles;
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TableProvider } from './internal/TableContext';
import { default as withStyles } from '../styles';
import { default as MuiTable } from '@mui/material/Table';
import { borderWidthThin, colorBrandNeutral300, colorBrandNeutral500, colorBrandNeutral600, fontSizeDefault, spacing30, spacing50, spacing80, spacing40, widthFluid } from '../styles/tokens';
import isNil from 'lodash/isNil';
var defaultResponsiveStyles = {
  border: 0,
  position: 'relative',
  '& thead': {
    // `display: 'none'` will not be read by screen readers
    // hence hiding it through visibility property.
    border: 'none',
    clip: 'rect(0 0 0 0)',
    height: borderWidthThin,
    margin: borderWidthThin,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: borderWidthThin
  },
  '& tbody tr': {
    display: 'flex',
    flexWrap: 'wrap',
    height: 'auto',
    marginBottom: spacing50,
    borderBottom: borderWidthThin + " solid " + colorBrandNeutral300,
    paddingBottom: spacing50,
    position: 'relative'
  },
  '& tbody td:before, & tbody th:before': {
    content: 'attr(data-label)',
    fontWeight: 'bold',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    left: 0,
    color: colorBrandNeutral600,
    float: 'left'
  },
  // set the fixed width of all cells, except the expansion chevron
  '& tbody td:not(.expand-row-button):before, & tbody th:not(.expand-row-button):before': {
    width: '6rem',
    minWidth: '6rem',
    marginRight: spacing50
  },
  '& tbody tr td, & tbody tr th': {
    color: colorBrandNeutral500
  },
  '& tbody td, & tbody th': {
    borderBottom: 'none',
    fontSize: fontSizeDefault,
    textAlign: 'left',
    width: widthFluid,
    padding: spacing30 + " 0",
    display: 'block'
  },
  '& tbody td > *, & tbody th > *': {
    display: 'flex'
  },
  '& tbody tr td.selection-column': {
    position: 'absolute',
    width: '30px',
    top: 0,
    right: 0,
    paddingRight: spacing40
  }
};
var expansionPanelStyles = (_expansionPanelStyles = {
  '& tbody tr': {
    paddingTop: spacing30 + " !important",
    paddingLeft: spacing30 + " !important",
    paddingBottom: spacing30 + " !important",
    marginBottom: '0 !important',
    borderColor: 'transparent'
  },
  '& tbody tr td, & tbody tr th': {
    wordBreak: 'break-word'
  }
}, _expansionPanelStyles["& thead th:last-child,\n        & tbody td:last-child,\n        & tbody th:last-child"] = {
  // On mobile should be always visible
  display: 'inherit !important'
}, _expansionPanelStyles['& tbody td:not(:first-child), & tbody th:not(:first-child)'] = {
  display: 'none'
}, _expansionPanelStyles["& tbody tr.table-row-expanded td:not(:first-child),\n        & tbody tr.table-row-expanded th:not(:first-child),\n        & tbody td.no-expanded-row-content button,\n        & tbody th.no-expanded-row-content span"] = {
  display: 'inherit'
}, _expansionPanelStyles['& tbody tr td.expand-row-button'] = {
  position: 'absolute',
  width: 'auto',
  top: 0,
  right: 0,
  paddingRight: spacing40
}, _expansionPanelStyles['& tbody tr td.selection-column'] = {
  position: 'absolute',
  width: '30px',
  top: '8px !important',
  right: '50px !important',
  paddingRight: spacing40
}, _expansionPanelStyles['& tbody tr.table-row-expanded-content'] = {
  paddingRight: spacing50
}, _expansionPanelStyles["& tbody tr.table-row-expanded-content td,\n        & tbody tr.table-row-expanded-content th"] = {
  wordBreak: 'break-word',
  paddingRight: 0
}, _expansionPanelStyles["& tbody tr.table-row-expanded-content td:before,\n        & tbody tr.table-row-expanded-content th:before"] = {
  display: 'none'
}, _expansionPanelStyles["& tbody tr.table-row-expanded-content td > *,\n        & tbody tr.table-row-expanded-content th > *"] = {
  display: 'inline-block'
}, _expansionPanelStyles);

// styles for responsive table with expansion panel and no row selection
var expansionPanelsNoSelectionStyles = {
  '& tbody tr': {
    paddingRight: spacing80 + " !important"
  }
};

// styles for responsive table with expansion panel and row selection
var expansionPanelsSelectionStyles = {
  '& tbody tr': {
    paddingRight: '5.5rem !important'
  },
  '& tbody td:nth-child(2)': {
    display: 'inherit !important'
  }
};

// styles for responsive table with row selection only
var responsiveTableRowSelection = {
  '& tbody tr': {
    paddingRight: spacing80 + " !important"
  }
};
var generateResponsiveBreakpoints = function generateResponsiveBreakpoints(breakpoints) {
  var styles = {};
  // Create responsive styles for each breakpoint
  breakpoints.keys.forEach(function (breakpoint, index) {
    if (breakpoint !== 'xl') {
      var _$responsive, _$expansionPanels, _$expansionPanelsSel, _$expansionPanelsNoS, _$responsiveTableRow, _styles;
      styles["responsive_" + breakpoint] = (_styles = {
        width: 'calc(100% - 0.8rem)'
      }, _styles["& tbody td.no-expanded-row-content button,\n                    & tbody th.no-expanded-row-content span"] = {
        display: 'none'
      }, _styles['&$responsive'] = (_$responsive = {}, _$responsive[breakpoints.down(breakpoints.keys[index + 1])] = _extends({}, defaultResponsiveStyles), _$responsive), _styles['&$expansionPanels'] = (_$expansionPanels = {}, _$expansionPanels[breakpoints.down(breakpoints.keys[index + 1])] = _extends({}, expansionPanelStyles), _$expansionPanels), _styles['&$expansionPanelsSelection'] = (_$expansionPanelsSel = {}, _$expansionPanelsSel[breakpoints.down(breakpoints.keys[index + 1])] = _extends({}, expansionPanelsSelectionStyles), _$expansionPanelsSel), _styles['&$expansionPanelsNoSelection'] = (_$expansionPanelsNoS = {}, _$expansionPanelsNoS[breakpoints.down(breakpoints.keys[index + 1])] = _extends({}, expansionPanelsNoSelectionStyles), _$expansionPanelsNoS), _styles['&$responsiveTableRowSelection'] = (_$responsiveTableRow = {}, _$responsiveTableRow[breakpoints.down(breakpoints.keys[index + 1])] = _extends({}, responsiveTableRowSelection), _$responsiveTableRow), _styles);
    } else {
      var _$responsive2, _$expansionPanels2, _$expansionPanelsSel2, _$expansionPanelsNoS2, _$responsiveTableRow2, _styles2;
      // When responsive='xl', it will always show
      // utilize .up() with the first breakpoint (which is xs = 0px)
      styles["responsive_" + breakpoint] = (_styles2 = {
        width: 'calc(100% - 0.8rem)'
      }, _styles2["& tbody td.no-expanded-row-content button,\n                    & tbody th.no-expanded-row-content span"] = {
        display: 'none'
      }, _styles2['&$responsive'] = (_$responsive2 = {}, _$responsive2[breakpoints.up(breakpoints.keys[0])] = _extends({}, defaultResponsiveStyles), _$responsive2), _styles2['&$expansionPanels'] = (_$expansionPanels2 = {}, _$expansionPanels2[breakpoints.up(breakpoints.keys[0])] = _extends({}, expansionPanelStyles), _$expansionPanels2), _styles2['&$expansionPanelsSelection'] = (_$expansionPanelsSel2 = {}, _$expansionPanelsSel2[breakpoints.up(breakpoints.keys[0])] = _extends({}, expansionPanelsSelectionStyles), _$expansionPanelsSel2), _styles2['&$expansionPanelsNoSelection'] = (_$expansionPanelsNoS2 = {}, _$expansionPanelsNoS2[breakpoints.up(breakpoints.keys[0])] = _extends({}, expansionPanelsNoSelectionStyles), _$expansionPanelsNoS2), _styles2['&$responsiveTableRowSelection'] = (_$responsiveTableRow2 = {}, _$responsiveTableRow2[breakpoints.up(breakpoints.keys[0])] = _extends({}, responsiveTableRowSelection), _$responsiveTableRow2), _styles2);
    }
  });
  return styles;
};
var styles = function styles(theme) {
  var _responsive;
  return _extends({
    // Necessary style overrides are set here
    // See Material UI documentation for available CSS classes to override
    root: {},
    stickyHeader: {},
    responsive: (_responsive = {
      width: 'calc(100% - 0.8rem)'
    }, _responsive["& tbody td.no-expanded-row-content button,\n            & tbody th.no-expanded-row-content span"] = {
      display: 'none'
    }, _responsive),
    expansionPanels: {},
    expansionPanelsSelection: {},
    expansionPanelsNoSelection: {},
    responsiveTableRowSelection: {}
  }, generateResponsiveBreakpoints(theme.breakpoints));
};
var ROW_SELECTION_ALL = TableProvider.ROW_SELECTION_ALL;
var ROW_SELECTION_NONE = TableProvider.ROW_SELECTION_NONE;
var ROW_SELECTION_SELECTED = TableProvider.ROW_SELECTION_SELECTED;
var ROW_SELECTION_UNSELECTED = TableProvider.ROW_SELECTION_UNSELECTED;
var ROW_SELECTION_SOME = TableProvider.ROW_SELECTION_SOME;

/**
 * Use `Table` to display sets of data within a grid.
 * @done true
 * @updated v7.9.0
 * @versionAdded v0.0.10
 * @examples
    TableExample
    TableWithPagination
    AnytimeEditableTableExample
    ResponsiveTableExample
    ResponsiveTableExpansionPanels
    ResponsiveTableExpandAll
    RowSelectionExample
    DisableIndividualRowSelectionExample
 */
var Table = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;
  var classes = props.classes,
    onExpand = props.onExpand,
    onClick = props.onClick,
    layoutProp = props.layout,
    children = props.children,
    rowSelectionEnabled = props.rowSelectionEnabled,
    rowSelectionOptions = props.rowSelectionOptions,
    selectedRowIds = props.selectedRowIds,
    onRowSelectionChange = props.onRowSelectionChange,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _useState = useState(new Set([])),
    localSelectedRowIds = _useState[0],
    setLocalSelectedRowIds = _useState[1];
  var _useState2 = useState(ROW_SELECTION_NONE),
    globalSelection = _useState2[0],
    setGlobalSelection = _useState2[1];
  var _useState3 = useState([]),
    rowData = _useState3[0],
    setRowData = _useState3[1];

  // tracks updates to row selections
  useEffect(function () {
    if (rowSelectionEnabled) {
      setLocalSelectedRowIds(new Set(selectedRowIds));
    }
  }, [selectedRowIds, rowSelectionEnabled]);

  // updates the select-all checkbox, based on current row selection state
  useEffect(function () {
    if (rowSelectionEnabled) {
      if (isNil(selectedRowIds) || selectedRowIds.length === 0) {
        setGlobalSelection(ROW_SELECTION_NONE);
      } else if (selectedRowIds.length === rowData.length) {
        setGlobalSelection(ROW_SELECTION_ALL);
      } else {
        setGlobalSelection(ROW_SELECTION_SOME);
      }
    }
  }, [rowSelectionEnabled, rowData, selectedRowIds]);

  // validates incoming props
  useEffect(function () {
    if (rowSelectionEnabled) {
      if (typeof onRowSelectionChange !== 'function') {
        console.error('onRowSelectionChange prop is required if rowSelectionEnabled is true');
      }
      if (isNil(selectedRowIds)) {
        console.error('selectedRowIds prop is required if rowSelectionEnabled is true');
      }
    }
  }, [rowSelectionEnabled, onRowSelectionChange, selectedRowIds]);

  // called by children when a selection is made
  var onSelectionUpdated = useCallback(function (selected, data) {
    if (typeof onRowSelectionChange === 'function') {
      // if no data is provided, this is a select all / select none
      if (data === undefined) {
        onRowSelectionChange({
          selectionType: selected ? ROW_SELECTION_ALL : ROW_SELECTION_NONE
        });

        // otherwise this is row selection
      } else {
        onRowSelectionChange({
          rowId: data.id,
          rowData: data,
          selectionType: selected ? ROW_SELECTION_SELECTED : ROW_SELECTION_UNSELECTED
        });
      }
    }
  }, [onRowSelectionChange]);

  // called by children to register the data associated with all rows (including row id)
  var registerRowData = useCallback(function (data) {
    setRowData(data);
  }, []);
  var layout = _extends({
    // declare default values to compare if consumer sent in a prop or not, but hide from prop API
    variant: '',
    breakpoint: ''
  }, layoutProp);
  var classOverrides = {
    root: classNames(classes.root, (_classNames = {}, _classNames[classes.responsive] = layoutProp, _classNames[classes.responsive_xs] = layout.breakpoint === 'xs', _classNames[classes.responsive_sm] = layout.breakpoint === 'sm', _classNames[classes.responsive_md] = layout.breakpoint === 'md', _classNames[classes.responsive_lg] = layout.breakpoint === 'lg', _classNames[classes.responsive_xl] = layout.variant !== '' && layout.breakpoint === '', _classNames[classes.expansionPanels] = layout.variant === 'expansionPanels', _classNames[classes.expansionPanelsNoSelection] = layout.variant === 'expansionPanels' && !rowSelectionEnabled, _classNames[classes.expansionPanelsSelection] = layout.variant === 'expansionPanels' && rowSelectionEnabled, _classNames[classes.responsiveTableRowSelection] = layout.variant === '' && rowSelectionEnabled, _classNames)),
    stickyHeader: classes.stickyHeader
  };
  var callback = function callback(e) {
    var targetClassList = e.target.classList;
    var isTargetExpandButton = targetClassList ? targetClassList.contains('expand-icon-container') || targetClassList.contains('expand-icon') : false;
    if (isTargetExpandButton && onExpand) {
      var target = e.currentTarget;

      // NOTE: The code coverage tool is not counting statements within `setTimeout()` for some reason.
      // Ignoring this for a while until we find a solution.
      // However, this block is always executed when expand all is clicked for which I have added UTs
      // TODO: Find how to get around `setTimeout()` for code coverage
      /* istanbul ignore next */
      // Break out of execution stack.
      setTimeout(function () {
        var rowsExpanded = target.querySelectorAll('.table-row-expanded');
        var rowsNotExpanded = target.querySelectorAll('.not-table-row-expanded');
        var rows = target.querySelectorAll('.table-expandable-row');
        if (rowsExpanded.length === rows.length) {
          onExpand(e, true);
        } else if (rowsNotExpanded.length === rows.length) {
          onExpand(e, false);
        }
      });
    }
    onClick && onClick(e);
  };
  var localChildren = React.Children.toArray(children);
  var tableToolbar;

  // look for a TableToolbar within the hierarchy of Table's immeidate children; if
  // there is one, pull it out and store it separately
  for (var i = 0; i < localChildren.length; i++) {
    if (localChildren[i].type.muiName === 'TableToolbar') {
      tableToolbar = localChildren.splice(i, 1);
      break;
    }
  }
  return /*#__PURE__*/React.createElement(TableProvider, {
    rowSelectionEnabled: rowSelectionEnabled,
    rowSelectionOptions: rowSelectionOptions,
    selectedRowIds: localSelectedRowIds,
    onSelectionUpdated: onSelectionUpdated,
    registerRowData: registerRowData,
    globalSelection: globalSelection,
    tableLayout: layout
  }, tableToolbar && tableToolbar, /*#__PURE__*/React.createElement(MuiTable, _extends({}, rest, {
    onClick: callback,
    classes: classOverrides,
    ref: ref
  }), localChildren));
});
Table.muiName = 'Table';
Table.displayName = 'Table';
Table.propTypes = {
  /**
   * The content of the table, normally `TableHeader` and `TableBody`.
   */
  children: PropTypes.node.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * Class name to be applied to the `Table` container.
   */
  className: PropTypes.string,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component: PropTypes.elementType,
  /**
   * Specify the cell size
   */
  size: PropTypes.oneOf(['small', 'medium']),
  /**
   * Set the header sticky.
   */
  stickyHeader: PropTypes.bool,
  /**
   * 	The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * Set the responsive layout `variant` and the `breakpoint` to trigger the responsive layout of the table.
   *
   * The shape of this prop is `{ variant: ['card', 'expansionPanels'], breakpoint: ['xs', 'sm', 'md', 'lg'] }`.
   *
   * If you do not specify a `breakpoint`, then the table will always show the given responsive layout variant.
   *
   * e.g.
   *
   * - `{ variant: 'card', breakpoint: 'sm' }` will result in the table rendering in the default table layout for viewports
   * greater than or equal to `sm` (768px) and the 'card' responsive layout when the viewport is smaller than `sm`.
   * - `{ variant: 'expansionPanels' }` will result in the table always showing the responsive expansion panel layout
   */
  layout: PropTypes.shape({
    variant: PropTypes.string,
    breakpoint: PropTypes.oneOf(['xs', 'sm', 'md', 'lg'])
  }),
  /**
   * Callback to toggle the boolean value
   */
  onExpand: PropTypes.func,
  /**
   * Callback triggered when expand all is clicked.
   */
  onClick: PropTypes.func,
  /**
   * Set to `true` to enable row selection. This will add a checkbox to the beginning of each row,
   * and enable mechanisms to track their selection state.
   * */
  rowSelectionEnabled: PropTypes.bool,
  /**
   * Provides options for when rowSelectionEnabled=true
   *
   * Available options are:
   *
   * * `hideSelectAll` (bool): true | **false**
   *      * hides the select-all checkbox in the table head
   *
   * */
  rowSelectionOptions: PropTypes.shape({
    hideSelectAll: PropTypes.bool
  }),
  /**
   * The ids of the selected rows in the table. The ids should match those passed into
   * the individual `TableRow` components (via the `rowData` prop).
   *
   * Works in concert with the `onRowSelectionChange` prop, which reports when the
   * user selects/deselects rows.
   *
   * Pass an empty array if no rows are selected.
   */
  selectedRowIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * A function that's invoked whenever a selection event occurs on the table.
   *
   * The function is passed a single argument. An object:
   *
   * * `rowId`: (`string`) The ID of the selected row. Only available for `ROW_SELECTION_SELECTED`
   *    and `ROW_SELECTION_UNSELECTED` events.
   * * `rowData`: (`object`) The full set of data supplied to the row (including the id). Only available
   *    ROW_SELECTION_SELECTED and `ROW_SELECTION_UNSELECTED` events.
   * * `selectionType`: (`string`) The type of row selection
   *
   * Valid values for `selectionType` are:
   *
   * * `ROW_SELECTION_ALL`
   * * `ROW_SELECTION_NONE`
   * * `ROW_SELECTION_SELECTED`
   * * `ROW_SELECTION_UNSELECTED`
   *
   */
  onRowSelectionChange: PropTypes.func
};

// default props
Table.defaultProps = {
  size: 'medium',
  rowSelectionEnabled: false
};
export { ROW_SELECTION_ALL, ROW_SELECTION_NONE, ROW_SELECTION_SELECTED, ROW_SELECTION_UNSELECTED };
export default withStyles(styles)(Table);